import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  CircularProgress,
  Chip,
  Collapse,
  Box,
  Badge,
  FormControlLabel,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ArrowDropDown as ArrowDropDownIcon, FilterAltOff, FilterAlt } from "@mui/icons-material";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import dayjs from "dayjs";
import { useCompany } from "context/CompanyContext";
import { useLanguage } from "context/LanguageContext";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import api from "services/api";

const FilterBar = ({ filters, setFilters, documentCount }) => {
  const { selectedCompany } = useCompany();
  const { t } = useLanguage();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  // Local states for filter values
  const [localDateType, setLocalDateType] = useState(filters.dateType || "invoice");
  const [localStartDate, setLocalStartDate] = useState(
    filters.startDate ? dayjs(filters.startDate) : null
  );
  const [localEndDate, setLocalEndDate] = useState(filters.endDate ? dayjs(filters.endDate) : null);
  const [localSupplierSearchText, setLocalSupplierSearchText] = useState("");
  const [localSupplierOptions, setLocalSupplierOptions] = useState([]);
  const [localSupplierLoading, setLocalSupplierLoading] = useState(false);
  const [localSelectedSuppliers, setLocalSelectedSuppliers] = useState(filters.suppliers || []);
  const [localXmlType, setLocalXmlType] = useState(filters.xmlType || "");
  const [localExcludeSuppliers, setLocalExcludeSuppliers] = useState(
    filters.excludeSuppliers || false
  );
  const [localDocumentTypes, setLocalDocumentTypes] = useState(filters.documentTypes || []);
  const [localExcludeDocumentTypes, setLocalExcludeDocumentTypes] = useState(
    filters.excludeDocumentTypes || false
  );
  const [localRecognitionStatus, setLocalRecognitionStatus] = useState(
    filters.recognitionStatus || ""
  );

  const translateWithFallback = (key, fallback) => {
    const translated = t ? t(key) : null;
    return translated && translated !== key ? translated : fallback;
  };

  const documentTypeOptions = useMemo(() => {
    return [
      {
        value: "purchase",
        label: translateWithFallback("purchaseDocument", "Pirkimo dokumentas"),
      },
      {
        value: "sale",
        label: translateWithFallback("salesDocument", "Pardavimo dokumentas"),
      },
      {
        value: "return",
        label: translateWithFallback("returnDocument", "Grąžinimo dokumentas"),
      },
      {
        value: "other",
        label: translateWithFallback("otherDocument", "Kitas dokumentas"),
      },
    ];
  }, [t]);

  const documentTypeLabelMap = useMemo(() => {
    return documentTypeOptions.reduce((acc, option) => {
      acc[option.value] = option.label;
      return acc;
    }, {});
  }, [documentTypeOptions]);

  const toggleFilterDisplay = () => {
    setIsFilterOpen((prev) => !prev);
  };

  useEffect(() => {
    const fetchSuppliers = async () => {
      if (!selectedCompany?.id) return;
      if (localSupplierSearchText.length >= 2) {
        setLocalSupplierLoading(true);
        try {
          // CSRF token (if required by backend)
          await api.get("/api/get-csrf-token/");

          const response = await api.get(
            `/api/get-supplier-variants/?search_term=${localSupplierSearchText}&company_id=${selectedCompany.id}`
          );

          if (response.data && response.data.suppliers) {
            setLocalSupplierOptions(response.data.suppliers);
          } else {
            setLocalSupplierOptions([]);
          }
        } catch (error) {
          console.error("Error fetching suppliers:", error);
          setLocalSupplierOptions([]);
        } finally {
          setLocalSupplierLoading(false);
        }
      } else {
        setLocalSupplierOptions([]);
      }
    };

    const debounceTimer = setTimeout(fetchSuppliers, 300);
    return () => clearTimeout(debounceTimer);
  }, [localSupplierSearchText, selectedCompany?.id]);

  const handleApplyFilters = () => {
    setFilters({
      ...filters,
      dateType: localDateType,
      startDate: localStartDate ? localStartDate.toISOString() : null,
      endDate: localEndDate ? localEndDate.toISOString() : null,
      suppliers: localSelectedSuppliers,
      excludeSuppliers: localExcludeSuppliers,
      xmlType: localXmlType,
      documentTypes: localDocumentTypes,
      excludeDocumentTypes: localExcludeDocumentTypes,
      recognitionStatus: localRecognitionStatus,
    });
    setIsFilterOpen(false);
  };

  const handleResetFilters = () => {
    setLocalDateType("invoice");
    setLocalStartDate(null);
    setLocalEndDate(null);
    setLocalSupplierSearchText("");
    setLocalSelectedSuppliers([]);
    setLocalXmlType("");
    setLocalExcludeSuppliers(false);
    setLocalDocumentTypes([]);
    setLocalExcludeDocumentTypes(false);
    setLocalRecognitionStatus("");
    setFilters({
      suppliers: [],
      dateType: "invoice",
      startDate: null,
      endDate: null,
      xmlType: "",
      excludeSuppliers: false,
      documentTypes: [],
      excludeDocumentTypes: false,
      recognitionStatus: "",
      searchQuery: filters.searchQuery,
    });
    setIsFilterOpen(false);
  };

  const handleSupplierChange = (event, newValue) => {
    setLocalSelectedSuppliers(newValue);
  };

  const handleDateTypeChange = (event) => {
    setLocalDateType(event.target.value);
  };

  const handleStartDateChange = (date) => {
    setLocalStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setLocalEndDate(date);
  };

  // Keep local UI state in sync when parent filters change (e.g., after navigation back)
  useEffect(() => {
    setLocalDateType(filters.dateType || "invoice");
    setLocalStartDate(filters.startDate ? dayjs(filters.startDate) : null);
    setLocalEndDate(filters.endDate ? dayjs(filters.endDate) : null);
    setLocalSelectedSuppliers(filters.suppliers || []);
    setLocalXmlType(filters.xmlType || "");
    setLocalExcludeSuppliers(!!filters.excludeSuppliers);
    setLocalDocumentTypes(filters.documentTypes || []);
    setLocalExcludeDocumentTypes(!!filters.excludeDocumentTypes);
    setLocalRecognitionStatus(filters.recognitionStatus || "");
  }, [filters]);

  return (
    <div>
      <Badge
        badgeContent={documentCount > 0 ? documentCount : null}
        color="primary"
        max={9999}
        sx={{
          "& .MuiBadge-badge": {
            fontSize: "0.75rem",
            height: "22px",
            minWidth: "22px",
            padding: "0 6px",
          },
        }}
      >
        <MDButton
          variant="contained"
          color="info"
          onClick={toggleFilterDisplay}
          endIcon={<ArrowDropDownIcon />}
          sx={{ minWidth: 120, boxShadow: 1, "&:hover": { boxShadow: 2 } }}
        >
          {t("filters")}
        </MDButton>
      </Badge>

      <Collapse in={isFilterOpen}>
        <MDBox
          display="flex"
          flexDirection="column"
          mt={2}
          sx={{ backgroundColor: "background.paper", p: 2, borderRadius: 1, boxShadow: 1 }}
        >
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: { xs: "100%", sm: 300 },
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              <Autocomplete
                multiple
                options={localSupplierOptions}
                value={localSelectedSuppliers}
                onChange={handleSupplierChange}
                onInputChange={(event, value) => setLocalSupplierSearchText(value)}
                getOptionLabel={(option) => option.supplier_name || ""}
                fullWidth
                size="small"
                limitTags={isSmDown ? 1 : 3}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("suppliers")}
                    placeholder={t("searchSuppliers")}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {localSupplierLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.supplier_name}
                      {...getTagProps({ index })}
                      size="small"
                      key={option.supplier_registration || `${option.supplier_name}-${index}`}
                    />
                  ))
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(e) => setLocalExcludeSuppliers(e.target.checked)}
                    color="primary"
                    checked={localExcludeSuppliers}
                  />
                }
                label={t("excludeSuppliers")}
                sx={{ mt: 1 }}
              />
            </Box>

            <TextField
              size="small"
              select
              label={t("dateType")}
              value={localDateType}
              onChange={handleDateTypeChange}
              SelectProps={{ native: true }}
              sx={{ minWidth: { xs: "100%", sm: 150 } }}
            >
              <option value="invoice">{t("invoiceDate")}</option>
              <option value="upload">{t("uploadDate")}</option>
            </TextField>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: { xs: "100%", sm: 220 },
                maxWidth: "100%",
              }}
            >
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>{t("documentType")}</InputLabel>
                <Select
                  multiple
                  value={localDocumentTypes}
                  onChange={(event) => {
                    const { value } = event.target;
                    setLocalDocumentTypes(typeof value === "string" ? value.split(",") : value);
                  }}
                  label={t("documentType")}
                  renderValue={(selected) =>
                    selected.length > 0
                      ? selected.map((type) => documentTypeLabelMap[type] || type).join(", ")
                      : translateWithFallback(
                          "allDocumentTypes",
                          translateWithFallback("all", "Visi dokumentų tipai")
                        )
                  }
                >
                  {documentTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={localDocumentTypes.indexOf(option.value) > -1} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(e) => setLocalExcludeDocumentTypes(e.target.checked)}
                    color="primary"
                    checked={localExcludeDocumentTypes}
                  />
                }
                label={translateWithFallback("excludeDocumentTypes", "Išskirti pasirinktus tipus")}
                sx={{ mt: 1 }}
              />
            </Box>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={t("startDate")}
                value={localStartDate}
                onChange={handleStartDateChange}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
              <DatePicker
                label={t("endDate")}
                value={localEndDate}
                onChange={handleEndDateChange}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </LocalizationProvider>
          </Box>

          <FormControl variant="outlined" size="small" fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t("xmlType")}</InputLabel>
            <Select
              value={localXmlType}
              onChange={(e) => setLocalXmlType(e.target.value)}
              label={t("xmlType")}
            >
              <MenuItem value="">{t("all")}</MenuItem>
              <MenuItem value="quantitative">Quantitative</MenuItem>
              <MenuItem value="summary">Summary</MenuItem>
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t("recognitionStatus") || "Atpažinimo būsena"}</InputLabel>
            <Select
              value={localRecognitionStatus}
              onChange={(e) => setLocalRecognitionStatus(e.target.value)}
              label={t("recognitionStatus") || "Atpažinimo būsena"}
            >
              <MenuItem value="">{t("all") || "Visi"}</MenuItem>
              <MenuItem value="unrecognized">{t("unrecognized") || "Neatpažinti"}</MenuItem>
              <MenuItem value="recognized">{t("recognized") || "Atpažinti"}</MenuItem>
            </Select>
          </FormControl>

          <MDBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
            <MDButton
              variant="contained"
              color="info"
              onClick={handleApplyFilters}
              startIcon={<FilterAlt />}
              sx={{ boxShadow: 1, width: { xs: "50%", sm: "auto" } }}
            >
              {t("apply")}
            </MDButton>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={handleResetFilters}
              startIcon={<FilterAltOff />}
              sx={{ width: { xs: "50%", sm: "auto" } }}
            >
              {t("resetFilters")}
            </MDButton>
          </MDBox>
        </MDBox>
      </Collapse>
    </div>
  );
};

FilterBar.propTypes = {
  filters: PropTypes.shape({
    suppliers: PropTypes.array,
    excludeSuppliers: PropTypes.bool,
    documentTypes: PropTypes.array,
    excludeDocumentTypes: PropTypes.bool,
    dateType: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    xmlType: PropTypes.string,
    searchQuery: PropTypes.string,
    recognitionStatus: PropTypes.string,
  }).isRequired,
  setFilters: PropTypes.func.isRequired,
  documentCount: PropTypes.number,
};

FilterBar.defaultProps = {
  documentCount: 0,
};

export default FilterBar;
